import React, { useMemo, useState } from 'react';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';
import { Country, countryCatalogByIsoCode, globalCountriesInfo } from './countryCatalog';

// Windows has no flag emoji: Segoe UI draws 🇮🇳 as the letters "IN". This font holds
// only the flag letters (unicode-range), so it can go first in a font stack and
// everything else still uses the theme font. The browser fetches it only when a flag shows.
const FLAG_FONT_FAMILY = 'Twemoji Country Flags';
const FLAG_FONT_URL =
  'https://cdn.jsdelivr.net/npm/country-flag-emoji-polyfill@0.1.8/dist/TwemojiCountryFlags.woff2';
if (typeof document !== 'undefined' && !document.getElementById('flag-emoji-font')) {
  const style = document.createElement('style');
  style.id = 'flag-emoji-font';
  style.textContent = `@font-face{font-family:"${FLAG_FONT_FAMILY}";src:url("${FLAG_FONT_URL}") format("woff2");unicode-range:U+1F1E6-1F1FF;font-display:swap;}`;
  document.head.appendChild(style);
}

/** Puts the flag font ahead of a font stack. Use it when restyling the field's text. */
export const withFlagFont = (fontFamily: string | undefined) =>
  fontFamily ? `"${FLAG_FONT_FAMILY}", ${fontFamily}` : `"${FLAG_FONT_FAMILY}"`;

const themeFlagFont = (theme: Theme) => withFlagFont(theme.typography.fontFamily);

export type CountryFlagProps = {
  country: Country;
  /** Flag width in px. Height follows the 7:5 flag shape. */
  width?: number;
  sx?: SxProps<Theme>;
};

/** A country's flag as a small rectangle, drawn from the flag emoji. */
export function CountryFlag({ country, width = 21, sx }: CountryFlagProps) {
  return (
    <Box
      component="span"
      aria-hidden
      data-testid="country-flag"
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width,
          height: Math.round((width * 5) / 7),
          overflow: 'hidden',
          borderRadius: '2px',
          // The flag glyph fills the em box's width and about 26/36 of its height.
          fontSize: width,
          lineHeight: 1,
          fontFamily: themeFlagFont,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {country.flag}
    </Box>
  );
}

type BaseAutocompleteProps = AutocompleteProps<Country, false, true, false>;

export type CountrySelectProps = Partial<
  Omit<BaseAutocompleteProps, 'options' | 'value' | 'onChange' | 'renderInput'>
> & {
  /** ISO 3166-1 alpha-2 code, e.g. "IN". */
  value?: string | null;
  onChange?: (countryCode: string | null, country: Country | null) => void;
  /** Visible label. Leave empty to show the field without one; ariaLabel still names it. */
  label?: string;
  ariaLabel?: string;
  /** Show "+91" in the field instead of "India". The flag sits before the text either way. */
  showPhoneCode?: boolean;
  /** Styles for the TextField, e.g. to restyle the outlined input. */
  inputSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
};

/**
 * Searchable country picker (flag, name, dial code).
 * Search matches country name, ISO code or dial code.
 */
export function CountrySelect({
  value,
  onChange,
  label = 'Country',
  ariaLabel,
  showPhoneCode = true,
  inputSx,
  sx,
  ...rest
}: CountrySelectProps) {
  const selected = useMemo(() => (value ? countryCatalogByIsoCode[value] ?? null : null), [value]);

  return (
    <Autocomplete<Country, false, true, false>
      options={globalCountriesInfo}
      // disableClearable types value as non-null; null still renders an empty field.
      value={selected as Country}
      onChange={(_, country) => onChange?.(country ? country.countryCode : null, country)}
      autoHighlight
      disableClearable
      isOptionEqualToValue={(o, v) => o.countryCode === v.countryCode}
      getOptionLabel={(o) => (showPhoneCode ? o.phoneCode : o.name)}
      filterOptions={(options, { inputValue }) => {
        const q = inputValue.trim().toLowerCase().replace(/^\+/, '');
        if (!q) return options;
        return options.filter(
          (o) =>
            o.name.toLowerCase().includes(q) ||
            o.countryCode.toLowerCase() === q ||
            o.phoneCode.slice(1).startsWith(q)
        );
      }}
      renderOption={(props, o) => {
        const { key, ...optionProps } = props;
        return (
          <Box
            component="li"
            key={o.countryCode}
            {...optionProps}
            sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}
          >
            <CountryFlag country={o} />
            <Box component="span" sx={{ flex: 1 }}>
              {o.name}
            </Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {o.phoneCode}
            </Box>
          </Box>
        );
      }}
      slotProps={{ popper: { placement: 'bottom-start', sx: { minWidth: 280 } } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || undefined}
          sx={inputSx}
          InputProps={{
            ...params.InputProps,
            startAdornment: selected ? <CountryFlag country={selected} sx={{ ml: 0.5 }} /> : null,
          }}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              autoComplete: 'new-password',
              'aria-label': ariaLabel ?? (label ? undefined : 'Country'),
            },
          }}
        />
      )}
      sx={sx}
      {...rest}
    />
  );
}

export type PhoneNumberValue = {
  countryCode: string;
  phoneCode: string;
  number: string;
  e164: string;
};

export type PhoneNumberFieldProps = {
  defaultCountry?: string;
  label?: string;
  onChange?: (value: PhoneNumberValue) => void;
  error?: boolean;
  helperText?: React.ReactNode;
};

/**
 * Phone input with a country dial-code picker.
 * onChange receives { countryCode, phoneCode, number, e164 }.
 */
export default function PhoneNumberField({
  defaultCountry = 'IN',
  label = 'Phone number',
  onChange,
  error,
  helperText,
}: PhoneNumberFieldProps) {
  const [countryCode, setCountryCode] = useState(defaultCountry);
  const [number, setNumber] = useState('');

  const emit = (cc: string, num: string) => {
    const phoneCode = countryCatalogByIsoCode[cc]?.phoneCode ?? '';
    onChange?.({
      countryCode: cc,
      phoneCode,
      number: num,
      e164: num ? `${phoneCode}${num}` : '',
    });
  };

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <CountrySelect
        value={countryCode}
        label="Code"
        onChange={(cc) => {
          if (!cc) return;
          setCountryCode(cc);
          emit(cc, number);
        }}
        sx={{ width: 140, flexShrink: 0 }}
      />
      <TextField
        fullWidth
        label={label}
        type="tel"
        value={number}
        error={error}
        helperText={helperText}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '').slice(0, 15);
          setNumber(digits);
          emit(countryCode, digits);
        }}
        slotProps={{ htmlInput: { inputMode: 'numeric' } }}
      />
    </Stack>
  );
}
